import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { AnimatePresence, easeOut, motion } from 'framer-motion';
import * as React from 'react';
import Chip from '../chip';
export type InputProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selectedValues: string[];
  options: Array<{
    label: string;
    value: string;
  }>;
  placeholder: string;
  hasError: boolean;
  disabled: boolean;
  isPopoverOpen: boolean;
  size: 'default' | 'compact';
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  toggleOption: (value: string) => void;
};
export const Input = React.forwardRef<HTMLButtonElement, InputProps>((t0, ref) => {
  const $ = _c(45);
  let isPopoverOpen;
  let onKeyDown;
  let options;
  let props;
  let selectedValues;
  let size;
  let t1;
  let t2;
  let t3;
  let toggleOption;
  if ($[0] !== t0) {
    ({
      selectedValues,
      options,
      placeholder: t1,
      hasError: t2,
      disabled: t3,
      isPopoverOpen,
      size,
      onKeyDown,
      toggleOption,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isPopoverOpen;
    $[2] = onKeyDown;
    $[3] = options;
    $[4] = props;
    $[5] = selectedValues;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = toggleOption;
  } else {
    isPopoverOpen = $[1];
    onKeyDown = $[2];
    options = $[3];
    props = $[4];
    selectedValues = $[5];
    size = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    toggleOption = $[10];
  }
  const placeholder = t1 === undefined ? "Select options" : t1;
  const hasError = t2 === undefined ? false : t2;
  const disabled = t3 === undefined ? false : t3;
  const t4 = size === "default";
  const t5 = size === "compact";
  const t6 = !hasError && !disabled;
  const t7 = !disabled;
  let t8;
  if ($[11] !== disabled || $[12] !== hasError || $[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = cx("w-full min-w-[240px]", "flex items-center justify-between w-full h-full px-1 border rounded-default group gap-x-2 focus-visible:outline-[var(--text-color-accent)] text-body-2", {
      "min-h-9 py-1": t4,
      "min-h-8 py-0.5": t5,
      "border-primary hover:border-primary": t6,
      "border-tertiary bg-secondary cursor-default": disabled,
      "cursor-pointer": t7,
      "border-error": hasError
    });
    $[11] = disabled;
    $[12] = hasError;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== options || $[19] !== placeholder || $[20] !== selectedValues || $[21] !== size || $[22] !== toggleOption) {
    t9 = selectedValues.length > 0 ? selectedValues.map(value => {
      const option = options.find(o => o.value === value);
      return <motion.div key={value} layout={true} initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }}><Chip label={option?.label} value={value} toggleOption={toggleOption} /></motion.div>;
    }) : <span className={cx("flex items-center text-body-2 text-tertiary", size === "compact" ? "mx-2" : "mx-3")}>{placeholder}</span>;
    $[18] = options;
    $[19] = placeholder;
    $[20] = selectedValues;
    $[21] = size;
    $[22] = toggleOption;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <motion.div className="flex flex-wrap gap-1"><AnimatePresence mode="popLayout">{t9}</AnimatePresence></motion.div>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = isPopoverOpen ? 180 : 0;
  let t12;
  if ($[26] !== t11) {
    t12 = {
      rotate: t11
    };
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      duration: 0.2,
      ease: easeOut
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = disabled ? "tertiary" : "primary";
  let t15;
  if ($[29] !== t14) {
    t15 = <IcoChevronDown16 color={t14} />;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t15) {
    t16 = <motion.div className="flex items-center justify-between mr-2" animate={t12} transition={t13}>{t15}</motion.div>;
    $[31] = t12;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== disabled || $[35] !== isPopoverOpen || $[36] !== onKeyDown || $[37] !== t10 || $[38] !== t16 || $[39] !== t8) {
    t17 = <button className={t8} tabIndex={0} onKeyDown={onKeyDown} aria-pressed={isPopoverOpen} type="button" aria-haspopup="listbox" aria-controls="multi-select-content" aria-expanded={isPopoverOpen} disabled={disabled} data-disabled={disabled}>{t10}{t16}</button>;
    $[34] = disabled;
    $[35] = isPopoverOpen;
    $[36] = onKeyDown;
    $[37] = t10;
    $[38] = t16;
    $[39] = t8;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== props || $[42] !== ref || $[43] !== t17) {
    t18 = <RadixPopover.Trigger {...props} ref={ref} tabIndex={0} asChild={true}>{t17}</RadixPopover.Trigger>;
    $[41] = props;
    $[42] = ref;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
});
export default Input;